import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { usersApi } from '../../api/users';
import { toUserRowViews } from './userAdapter';
import {
  DEFAULT_PARAMS,
  MIN_SEARCH_LENGTH,
  parseUsersParams,
  toQueryString,
  updateParams,
} from './usersParams';

const STALE_TIME = 30_000;
const SEARCH_DEBOUNCE_MS = 300;

export const usersQueryKey = (params) => ['users', params];

/** Retry once for server/network trouble; client errors (4xx) won't fix themselves. */
function shouldRetry(failureCount, error) {
  const status = error?.status;
  return (status === 0 || status >= 500) && failureCount < 1;
}

/**
 * Users list state, synced with the URL query string, plus the data for it.
 * Search input is debounced and only applied at ≥ 2 characters (empty clears it).
 */
export default function useUsers() {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { params, invalid } = useMemo(() => parseUsersParams(searchParams), [searchParams]);

  const writeParams = useCallback(
    (next, { replace = false } = {}) => setSearchParams(toQueryString(next), { replace }),
    [setSearchParams],
  );

  // Clean up hand-edited / stale URLs (e.g. ?status=bogus) without adding a history entry
  useEffect(() => {
    if (invalid.length) writeParams(params, { replace: true });
  }, [invalid, params, writeParams]);

  const setParams = useCallback((changes) => writeParams(updateParams(params, changes)), [params, writeParams]);

  // --- search box (local while typing, URL once debounced) ---
  const [searchInput, setSearchInput] = useState(params.search);
  const lastApplied = useRef(params.search);

  // Back/forward or a shared link changed the search: mirror it in the box
  useEffect(() => {
    if (params.search !== lastApplied.current) {
      lastApplied.current = params.search;
      setSearchInput(params.search);
    }
  }, [params.search]);

  useEffect(() => {
    const term = searchInput.trim();
    if (term.length > 0 && term.length < MIN_SEARCH_LENGTH) return undefined;
    if (term === params.search) return undefined;
    const timer = setTimeout(() => {
      lastApplied.current = term;
      setParams({ search: term });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, params.search, setParams]);

  /** Clears the search (and any `extraChanges`, e.g. filters) in one URL update. */
  const clearSearch = useCallback(
    (extraChanges = {}) => {
      setSearchInput('');
      lastApplied.current = '';
      setParams({ search: '', ...extraChanges });
    },
    [setParams],
  );

  // --- data ---
  const query = useQuery({
    queryKey: usersQueryKey(params),
    queryFn: ({ signal }) => usersApi.list(params, { signal }),
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
    retry: shouldRetry,
  });
  const { data, error, isPending, isFetching, isPlaceholderData, refetch } = query;

  // Prefetch the next page so "Next" feels instant
  useEffect(() => {
    if (!data?.pagination.hasNextPage || isPlaceholderData) return;
    const nextParams = { ...params, page: params.page + 1 };
    queryClient.prefetchQuery({
      queryKey: usersQueryKey(nextParams),
      queryFn: ({ signal }) => usersApi.list(nextParams, { signal }),
      staleTime: STALE_TIME,
    });
  }, [data, isPlaceholderData, params, queryClient]);

  // Page beyond the end (e.g. after filtering): jump to the last page that exists
  useEffect(() => {
    if (!data || isPlaceholderData) return;
    const { totalPages } = data.pagination;
    const lastPage = Math.max(1, totalPages);
    if (params.page > lastPage) writeParams({ ...params, page: lastPage }, { replace: true });
  }, [data, isPlaceholderData, params, writeParams]);

  // 400: show the backend's message, reset the offending params and retry once
  const resetFor = useRef(null);
  useEffect(() => {
    if (error?.status !== 400) return;
    const key = toQueryString(params);
    if (resetFor.current === key) return; // already retried for this URL; show the error state
    resetFor.current = key;
    toast.error(error.message);
    const fields = error.fieldErrors.map((e) => e.field).filter((f) => f in DEFAULT_PARAMS);
    const reset = Object.fromEntries((fields.length ? fields : Object.keys(DEFAULT_PARAMS)).map((f) => [f, DEFAULT_PARAMS[f]]));
    writeParams({ ...params, ...reset }, { replace: true });
  }, [error, params, writeParams]);

  const rows = useMemo(
    () => (data ? toUserRowViews(data.users, { page: data.pagination.page, limit: data.pagination.limit }) : []),
    [data],
  );

  return {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearSearch,
    rows,
    pagination: data?.pagination ?? null,
    totalRegistered: data?.totalRegistered ?? null,
    /** First load with nothing to show yet */
    isInitialLoading: isPending && !data,
    /** Fetching another page/sort while the previous rows stay on screen */
    isRefreshing: isFetching && isPlaceholderData,
    error: data && !isPlaceholderData ? null : error,
    refetch,
  };
}
